import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { useAuthStore } from '../../stores/authStore';
import { apiClient } from '../../services/api';
import { tokenManager } from '../../services/authService';

interface ProfileUpdateDto {
  firstName: string;
  lastName: string;
  phone: string;
}

export const PersonalInfoSettings: React.FC = () => {
  const { user, refreshUser } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState<ProfileUpdateDto>({
    firstName: '',
    lastName: '',
    phone: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const handleChange = (field: keyof ProfileUpdateDto) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await apiClient.patch('/users/me', formData);

      // Update local storage with new user data
      const currentUser = tokenManager.getUser();
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
        };
        const accessToken = tokenManager.getAccessToken();
        const refreshToken = tokenManager.getRefreshToken();
        if (accessToken && refreshToken) {
          tokenManager.setTokens(accessToken, refreshToken, updatedUser);
        }
      }

      refreshUser();
      setSuccessMessage('Profile updated successfully');
      setIsEditing(false);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
      });
    }
    setIsEditing(false);
    setErrorMessage(null);
  };

  if (!user) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <PersonOutlinedIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Personal Information</Typography>
          </Box>
          {!isEditing && (
            <Button variant="outlined" size="small" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}
        </Box>

        {successMessage && (
          <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={formData.firstName}
              onChange={handleChange('firstName')}
              disabled={!isEditing}
              variant={isEditing ? 'outlined' : 'filled'}
              InputProps={{
                readOnly: !isEditing,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={formData.lastName}
              onChange={handleChange('lastName')}
              disabled={!isEditing}
              variant={isEditing ? 'outlined' : 'filled'}
              InputProps={{
                readOnly: !isEditing,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              value={user.email}
              disabled
              variant="filled"
              InputProps={{
                readOnly: true,
              }}
              helperText="Email cannot be changed"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone}
              onChange={handleChange('phone')}
              disabled={!isEditing}
              variant={isEditing ? 'outlined' : 'filled'}
              InputProps={{
                readOnly: !isEditing,
              }}
              placeholder="+213 555 123456"
            />
          </Grid>
        </Grid>

        {isEditing && (
          <Box display="flex" gap={2} mt={3}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} /> : null}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outlined" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
